// Your own air-bike VO2max, calibrated to your rowing one. No published
// equation turns an air bike's watts into oxygen (arms and legs both work,
// and the console estimates its watts from the fan), so Khronos learns yours:
// on steady air-bike rides near a 2k test, your heart rate says what share of
// that 2k-based VO2max you were using, which sets your oxygen cost per watt
// on that bike. Later rides use that cost the same way the bike estimate
// uses the ACSM equation (utils/vo2max). A personal method, not a published
// one: it tracks change against your rowing figure.

import { currentVo2max, vo2maxFromSubmax } from "./vo2max";

export const CALIBRATION_DAYS = 28; // rides this close to the 2k calibrate
const DAY_MS = 86400000;
const BASE = 7; // ml/kg/min at rest plus unloaded movement, as for a bike

const median = (xs) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** The oxygen cost (ml/kg/min per W/kg) one steady ride implies. */
const costFrom = (steady, anchor, kg) => {
  const vo2 = 3.5 + steady.pct_hrr * (anchor - 3.5);
  return (vo2 - BASE) / (steady.watts / kg);
};

/**
 * @param {{ rows: Array<{ id, started_at: string, air_steady?: { watts: number, pct_hrr: number } }>,
 *   anchor: null | { value: number, date: Date|null }, kg: number }} input
 * @returns {null | { cost: number, rides: number, estimates: Array<{ id, started_at: string, value: number }>,
 *   current: null | { value: number, count: number } }}
 */
export function calibrateAirBike({ rows, anchor, kg }) {
  if (!anchor?.value || !anchor.date || !(kg > 0)) return null;
  const air = (rows || []).filter((r) => r?.air_steady?.watts > 0 && r.air_steady.pct_hrr > 0 && r.started_at);
  const near = air.filter(
    (r) => Math.abs(new Date(r.started_at).getTime() - anchor.date.getTime()) <= CALIBRATION_DAYS * DAY_MS,
  );
  if (near.length === 0) return null;
  const cost = median(near.map((r) => costFrom(r.air_steady, anchor.value, kg)));
  if (!(cost > 0)) return null;
  const estimates = air.map((r) => ({
    id: r.id,
    started_at: r.started_at,
    value: Math.round(vo2maxFromSubmax(cost * (r.air_steady.watts / kg) + BASE, r.air_steady.pct_hrr) * 10) / 10,
  }));
  const current = currentVo2max(estimates.map((e) => ({ started_at: e.started_at, vo2max: { value: e.value } })));
  return { cost: Math.round(cost * 100) / 100, rides: near.length, estimates, current };
}
